import { Task, TaskStatus } from '../types';
import {
  getFlyerTasks,
  checkFlyerTask,
  claimFlyerTask,
  getTraffyTasks,
  getTgrassTasks,
  getSubgramTasks,
} from '../../../api/modules/Tasks/flyer';
import {
  getFreezedTasks,
  claimBarzhaTask,
  checkBarzhaTask,
} from '../../../api/modules/Tasks/barzha';
import {
  getSponsorsTasks,
  claimSponsorTask,
} from '../../../api/modules/Tasks/sponsors';
import { getGameTasks } from '../../../api/modules/Tasks/game_tasks';
import {
  getBannerCpmTasks,
  claimBannerCpmReward,
} from '../../../api/modules/Tasks/banner_cpm';
import {
  claimBannerCpcTask,
  getBannerCpcTasks,
} from '../../../api/modules/Tasks/banner_cpc';

// import { TraffyAdapter } from '../adapters/sponsors/TraffyAdapter';
// import { FlyerAdapter } from '../adapters/sponsors/FlyerAdapter';
import { BannerCpmAdapter } from '../adapters/sponsors/BannerCpmAdapter';
import { BannerCpcAdapter } from '../adapters/sponsors/BannerCpcAdapter';
import { GameTasksAdapter } from '../adapters/sponsors/GameTasksAdapter';
import {
  SponsorsTasksAdapter,
  SponsorType,
} from '../adapters/SponsorsTasksAdapter';
import { checkSponsorTask } from '../../../api/modules/Tasks/sponsors';
import { useNotificationStore } from '../../../store/NotificationStore';
import { MonetagBoostProvider } from '../adapters/sponsors/boost/MonetagBoostProvider';

export class SponsorsService {
  private static tasks: Task[] = [];
  private static onTasksUpdate: ((tasks: Task[]) => void) | null = null;
  private static flyerCheckAttempts: Map<string | number, number> = new Map();
  private static readonly MAX_FLYER_CHECK_ATTEMPTS = 3;
  static setOnTasksUpdate(callback: (tasks: Task[]) => void) {
    this.onTasksUpdate = callback;
  }

  private static notifyListeners() {
    console.log('🔔 Уведомляем слушателей об изменениях');
    if (this.onTasksUpdate) {
      // Создаем новый массив для гарантированного обновления
      this.onTasksUpdate([...this.tasks]);
    }
  }

  static updateTask(task: Task) {
    console.log('📝 SponsorsService.updateTask - start:', task);

    // Находим индекс задания по id и provider
    const index = this.tasks.findIndex(
      (t) => t.id === task.id && t.provider === task.provider,
    );
    console.log('🔍 Найден индекс задания:', index);

    if (index !== -1) {
      // Обновляем задание
      this.tasks[index] = task;
      console.log('✅ Задание обновлено:', this.tasks[index]);

      // Создаем новый массив для гарантированного обновления
      this.tasks = [...this.tasks];

      // Уведомляем слушателей об изменениях
      this.notifyListeners();
    } else {
      console.log('❌ Задание не найдено в списке');
      // Если задание не найдено, добавляем его в список
      this.tasks.push(task);
      console.log('✅ Задание добавлено в список:', task);

      // Создаем новый массив для гарантированного обновления
      this.tasks = [...this.tasks];

      // Уведомляем слушателей об изменениях
      this.notifyListeners();
    }
  }

  static updateTasks(newTasks: Task[]) {
    console.log('📝 SponsorsService.updateTasks - start:', newTasks);

    // Создаем Map для быстрого поиска заданий по ID
    const taskMap = new Map<string | number, Task>();

    // Добавляем существующие задания в Map
    this.tasks.forEach((task) => {
      taskMap.set(task.id, task);
    });

    // Обновляем или добавляем новые задания
    newTasks.forEach((task) => {
      taskMap.set(task.id, task);
    });

    // Преобразуем Map обратно в массив и создаем новый массив
    this.tasks = [...Array.from(taskMap.values())];
    console.log('✅ Список заданий обновлен:', this.tasks);

    // Сортируем задания
    this.tasks = this.sortTasksByStatus(this.tasks);

    // Уведомляем об изменениях
    this.notifyListeners();
  }

  private static async getBannerTasks(): Promise<Task[]> {
    try {
      const [cpmResponse, cpcResponse] = await Promise.all([
        getBannerCpmTasks(),
        getBannerCpcTasks(),
      ]);

      const cpmTasks = BannerCpmAdapter.adaptTasks(cpmResponse);
      const cpcTasks = BannerCpcAdapter.adaptTasks(cpcResponse);

      return [...cpmTasks, ...cpcTasks];
    } catch (error) {
      console.error('❌ Error fetching Banner tasks:', error);
      return [];
    }
  }

  private static async getSponsorsTasks(): Promise<Task[]> {
    try {
      const response = await getSponsorsTasks();
      return SponsorsTasksAdapter.adaptTasks(
        SponsorType.SPONSORS,
        response as any,
        [],
      );
    } catch (error) {
      console.error('❌ Error fetching Sponsors tasks:', error);
      return [];
    }
  }

  private static async getGameTasks(): Promise<Task[]> {
    try {
      const response = await getGameTasks();
      return GameTasksAdapter.adaptTasks(response as any);
    } catch (error) {
      console.error('❌ Error fetching Game tasks:', error);
      return [];
    }
  }

  private static async getFlyerTasks(): Promise<Task[]> {
    console.log('🚀 SponsorsService.getFlyerTasks - start');

    // Выполняем все API вызовы параллельно с обработкой ошибок
    const [flyerResult, traffyResult, tgrassResult, subgramResult] =
      await Promise.allSettled([
        getFlyerTasks().catch((error) => {
          console.error('❌ Flyer API error:', error);
          return { tasks: [] };
        }),
        getTraffyTasks().catch((error) => {
          console.error('❌ Traffy API error:', error);
          return { tasks: [] };
        }),
        getTgrassTasks().catch((error) => {
          console.error('❌ Tgrass API error:', error);
          return { tasks: [] };
        }),
        getSubgramTasks().catch((error) => {
          console.error('❌ Subgram API error:', error);
          return { tasks: [] };
        }),
      ]);

    // Извлекаем данные из результатов
    const flyerData =
      flyerResult.status === 'fulfilled' ? flyerResult.value : { tasks: [] };
    const traffyData =
      traffyResult.status === 'fulfilled' ? traffyResult.value : { tasks: [] };
    const tgrassData =
      tgrassResult.status === 'fulfilled' ? tgrassResult.value : { tasks: [] };
    const subgramData =
      subgramResult.status === 'fulfilled'
        ? subgramResult.value
        : { tasks: [] };

    console.log('📦 Flyer response:', flyerData);
    console.log('📦 Traffy response:', traffyData);
    console.log('📦 Tgrass response:', tgrassData);
    console.log('📦 Subgram response:', subgramData);

    const combinedResponse = [
      ...(flyerData.tasks || []),
      ...(traffyData.tasks || []),
      ...(tgrassData.tasks || []),
      ...(subgramData.tasks || []),
    ];

    try {
      const adaptedTasks = await SponsorsTasksAdapter.adaptTasks(
        SponsorType.FLYER,
        { tasks: combinedResponse } as any,
        [],
      );
      console.log('✅ Adapted Flyer tasks:', adaptedTasks);
      return adaptedTasks;
    } catch (error) {
      console.error('❌ Error adapting Flyer tasks:', error);
      return [];
    }
  }

  private static async getBarzhaTasks(): Promise<Task[]> {
    try {
      const response = await getFreezedTasks();
      return SponsorsTasksAdapter.adaptTasks(
        SponsorType.BARZHA,
        response as any,
        [],
      );
    } catch (error) {
      console.error('❌ Error fetching Barzha tasks:', error);
      return [];
    }
  }

  private static async getMonetagTasks(): Promise<Task[]> {
    try {
      const monetagProvider = new MonetagBoostProvider();
      const boostTasks = await monetagProvider.getAvailableTasks();
      // Преобразуем BoostTask в Task, если нужно (минимально)
      return boostTasks.map((t) => ({
        id: t.id,
        title: t.title || 'Показать рекламу',
        description: t.description || '',
        provider: 'SPONSORS',
        buttonText: t.title || 'Показать рекламу',
        status: 'NOT_STARTED',
        type: 'sponsors',
        isButtonEnabled: true,
        visibility: true,
        onClick: t.onClick,
      }));
    } catch (error) {
      console.error('❌ Error fetching Monetag tasks:', error);
      return [];
    }
  }

  static async getSponsorTasks(): Promise<Task[]> {
    console.log('🚀 SponsorsService.getSponsorTasks - start');

    try {
      // Получаем задания от всех провайдеров параллельно
      const [
        bannerTasks,
        flyerTasks,
        barzhaTasks,
        sponsorsTasks,
        monetagTasks,
        gameTasks,
      ] = await Promise.allSettled([
        this.getMonetagTasks(),
        this.getBannerTasks(),
        this.getFlyerTasks(),
        this.getBarzhaTasks(),
        this.getSponsorsTasks(),
        this.getGameTasks(),
      ]);

      // Обрабатываем результаты
      const tasks: Task[] = [];

      // Обрабатываем Banner задания
      if (bannerTasks.status === 'fulfilled') {
        tasks.push(...bannerTasks.value);
      } else {
        console.error('❌ Error fetching Banner tasks:', bannerTasks.reason);
      }

      // Обрабатываем Flyer задания
      if (flyerTasks.status === 'fulfilled') {
        console.log('✅ Flyer tasks fulfilled:', flyerTasks.value);
        console.log('📊 Flyer tasks count:', flyerTasks.value.length);
        console.log(
          '📋 Flyer tasks with rewards:',
          flyerTasks.value.filter(
            (task) => task.rewards?.stars && task.rewards.stars > 0,
          ),
        );
        tasks.push(...flyerTasks.value);
      } else {
        console.error('❌ Error fetching Flyer tasks:', flyerTasks.reason);
      }

      // Обрабатываем Barzha задания
      if (barzhaTasks.status === 'fulfilled') {
        tasks.push(...barzhaTasks.value);
      } else {
        console.error('❌ Error fetching Barzha tasks:', barzhaTasks.reason);
      }

      // Обрабатываем Sponsors задания
      if (sponsorsTasks.status === 'fulfilled') {
        tasks.push(...sponsorsTasks.value);
      } else {
        console.error(
          '❌ Error fetching Sponsors tasks:',
          sponsorsTasks.reason,
        );
      }

      // Обрабатываем Monetag задания
      if (monetagTasks.status === 'fulfilled') {
        tasks.push(...monetagTasks.value);
      } else {
        console.error('❌ Error fetching Monetag tasks:', monetagTasks.reason);
      }

      // Обрабатываем Game задания
      if (gameTasks.status === 'fulfilled') {
        tasks.push(...gameTasks.value);
      } else {
        console.error('❌ Error fetching Game tasks:', gameTasks.reason);
      }

      console.log('📊 Total tasks before sorting:', tasks.length);
      console.log('📋 All tasks:', tasks);

      // Сортируем задания
      this.tasks = this.sortTasksByStatus(tasks);
      console.log('📊 Total tasks after sorting:', this.tasks.length);
      console.log('📋 Sorted tasks:', this.tasks);

      // Уведомляем об изменениях
      this.notifyListeners();
      return this.tasks;
    } catch (error) {
      console.error('❌ SponsorsService.getSponsorTasks - error:', error);
      return [];
    }
  }

  // Метод для проверки статуса задания Flyer
  static async checkFlyerTaskStatus(taskId: string | number): Promise<any> {
    const attempts = this.flyerCheckAttempts.get(taskId) || 0;

    if (attempts >= this.MAX_FLYER_CHECK_ATTEMPTS) {
      console.log(
        'ℹ️ Превышено максимальное количество попыток проверки Flyer задания',
      );
      return;
    }

    try {
      const res = await checkFlyerTask(taskId.toString());
      this.flyerCheckAttempts.set(taskId, attempts + 1);
      return res;
    } catch (error) {
      console.error('❌ SponsorsService.checkFlyerTaskStatus - error:', error);

      throw error;
    }
  }

  // Метод для обновления статуса задания Flyer
  static updateFlyerTaskStatus(
    taskId: string | number,
    status: TaskStatus,
  ): void {
    console.log('🎯 updateFlyerTaskStatus called with:', { taskId, status });
    console.log('📋 Current tasks:', this.tasks);

    const taskIndex = this.tasks.findIndex((task) => {
      const matches =
        task.provider === 'FLYER' && String(task.id) === String(taskId);
      console.log('🔍 Checking task:', { task, matches });
      return matches;
    });

    console.log('📌 Found task at index:', taskIndex);

    if (taskIndex !== -1) {
      console.log('🔄 Updating Flyer task status:', { taskId, status });
      this.tasks[taskIndex].status = status;
      // Обновляем текст кнопки в зависимости от статуса
      switch (status) {
        case 'NOT_STARTED':
          this.tasks[taskIndex].buttonText = 'Выполнить';
          break;
        case 'IN_PROGRESS':
          this.tasks[taskIndex].buttonText = 'Проверка';
          break;
        case 'CHECK':
          this.tasks[taskIndex].buttonText = 'Проверить';

          break;
        case 'DONE':
          this.tasks[taskIndex].buttonText = 'Собрать';
          break;
        case 'CLAIMED':
          this.tasks[taskIndex].buttonText = 'Завершено';
          break;
        default:
          this.tasks[taskIndex].buttonText = 'Выполнить';
      }
      console.log('✅ Task updated:', this.tasks[taskIndex]);
      console.log('🔔 Calling notifyListeners');
      this.notifyListeners();
      console.log('✅ notifyListeners called');

      // Показываем уведомление только если статус CLAIMED
      if (status === 'CLAIMED') {
        const { setNotification } = useNotificationStore.getState();
        setNotification({
          type: 'Success',
          message: 'Задание выполнено!',
        });
      }
    } else {
      console.warn('⚠️ Task not found:', { taskId, status });
    }
  }

  private static sortTasksByStatus(tasks: Task[]): Task[] {
    // Обновляем приоритет статусов, чтобы DONE был в самом низу
    const statusPriority: Record<TaskStatus, number> = {
      NOT_STARTED: 1,
      IN_PROGRESS: 2,
      CHECK: 3,
      DONE: 4,
      CLAIMED: 5,
      SLEEP: 6,
    };

    const sortByStatus = (a: Task, b: Task) =>
      statusPriority[a.status] - statusPriority[b.status];

    // Объединяем все задания в один массив и сортируем их по статусу
    return tasks.sort(sortByStatus);
  }

  static async claimSponsorTask(taskId: string | number): Promise<void> {
    console.log('🎁 Claiming task:', taskId);

    // Находим задание по ID (нормализуем тип значения ID)
    const task = this.tasks.find((t) => String(t.id) === String(taskId));
    if (!task) {
      console.error('❌ Task not found:', taskId);
      return;
    }

    if (task.type === 'sponsor-subs') {
      await claimSponsorTask(Number(taskId));
      task.status = 'CLAIMED';
      this.notifyListeners();
      const { setNotification } = useNotificationStore.getState();
      setNotification({
        type: 'Success',
        message: 'Награда успешно получена!',
      });
      task.buttonText = 'Завершено';
      return;
    }

    // В зависимости от провайдера вызываем соответствующий метод
    switch (task.provider) {
      case 'FLYER':
        await claimFlyerTask(taskId.toString());
        break;
      case 'BARZHA':
        // await checkBarzhaTask(Number(taskId));
        await claimBarzhaTask(Number(taskId));
        break;
      // case 'BANNER_CPM':
      //   await claimBannerCpmReward(Number(taskId));
      //   break;
      // case 'BANNER_CPC':
      //   await claimBannerCpcTask(Number(taskId));
      //   break;
      case 'SPONSORS':
        await claimSponsorTask(Number(taskId));
        break;
      case 'GAME_TASKS':
        await claimSponsorTask(Number(taskId));
        break;
      default:
        console.error('❌ Unknown provider:', task.provider);
        return;
    }

    // Обновляем статус задания
    task.status = 'CLAIMED';
    this.notifyListeners();

    // Уведомление о получении награды
    const { setNotification } = useNotificationStore.getState();
    setNotification({
      type: 'Success',
      message: 'Награда успешно получена!',
    });
  }

  // Метод для проверки статуса задания Barzha
  static async checkBarzhaTask(taskId: string | number): Promise<void> {
    try {
      const response = await checkBarzhaTask(Number(taskId));
      console.log('✅ Barzha task check result:', response);

      // Обновляем статус задания
      const task = this.tasks.find((t) => t.id === taskId);
      if (task) {
        // Если статус claim, меняем текст кнопки на "Собрать"
        if (response?.data?.status === 'claim') {
          task.buttonText = 'Собрать';
          // Автоматически делаем claim
          await claimBarzhaTask(Number(taskId));
          task.status = 'CLAIMED';
        } else {
          task.status = 'DONE';
        }
        this.notifyListeners();
      }
    } catch (error) {
      console.error('❌ SponsorsService.checkBarzhaTask - error:', error);
      throw error;
    }
  }

  static async checkSponsorTask(type: string, taskId: number): Promise<void> {
    try {
      const response = await checkSponsorTask(type, taskId);
      console.log('✅ Sponsor task check result:', response);

      // Обновляем статус задания
      const task = this.tasks.find((t) => t.id === taskId);
      if (task) {
        if (response?.status === 'done') {
          task.status = 'DONE';
          task.buttonText = 'Собрать';
        } else {
          task.status = 'IN_PROGRESS';
          task.buttonText = 'Проверка';
        }
        this.notifyListeners();
      }
    } catch (error) {
      console.error('❌ SponsorsService.checkSponsorTask - error:', error);
      throw error;
    }
  }

  static updateSponsorTaskStatus(
    taskId: string | number,
    status: TaskStatus,
  ): void {
    console.log('🎯 updateSponsorTaskStatus called with:', { taskId, status });
    console.log('📋 Current tasks:', this.tasks);

    const taskIndex = this.tasks.findIndex((task) => {
      const matches =
        task.provider === 'SPONSORS' && String(task.id) === String(taskId);
      console.log('🔍 Checking task:', { task, matches });
      return matches;
    });

    console.log('📌 Found task at index:', taskIndex);

    if (taskIndex !== -1) {
      console.log('🔄 Updating Sponsor task status:', { taskId, status });
      this.tasks[taskIndex].status = status;
      // Обновляем текст кнопки в зависимости от статуса
      switch (status) {
        case 'NOT_STARTED':
          this.tasks[taskIndex].buttonText = 'Выполнить';
          break;
        case 'IN_PROGRESS':
          this.tasks[taskIndex].buttonText = 'Проверка';
          break;
        case 'CHECK':
          this.tasks[taskIndex].buttonText = 'Проверить';
          break;
        case 'DONE':
          this.tasks[taskIndex].buttonText = 'Собрать';
          break;
        case 'CLAIMED':
          this.tasks[taskIndex].buttonText = 'Завершено';
          break;
        default:
          this.tasks[taskIndex].buttonText = 'Выполнить';
      }
      console.log('✅ Task updated:', this.tasks[taskIndex]);
      console.log('🔔 Calling notifyListeners');
      this.notifyListeners();
      console.log('✅ notifyListeners called');

      // Если задание на проверке
      const { setNotification } = useNotificationStore.getState();
      setNotification({
        type: 'Warning',
        message: 'Задание на проверке.',
      });
    } else {
      console.warn('⚠️ Task not found:', { taskId, status });
      const { setNotification } = useNotificationStore.getState();
      setNotification({
        type: 'Error',
        message: 'Задание не найдено. Пожалуйста, проверьте ID задания.',
      });
    }
  }

  // Уведомление о невыполнении условий
  static notifyUnfulfilledConditions(): void {
    const { setNotification } = useNotificationStore.getState();
    setNotification({
      type: 'Error',
      message: 'Ты не выполнил условия.',
    });
  }
}
